import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  SpeedDial,
  SpeedDialAction,
  Tooltip,
  Typography,
  Skeleton,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import { PiTreeStructureFill } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { ASSETS_BASE_URL, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from "../../config";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useSite } from "../../context/siteContext";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import ContactCCRForm from "../ContactCCRForm/ContactCCRForm";
import MachineNotifyForm from "../MachineNotifyForm/MachineNotifyForm";
import ModelButton from "../ModelButton/ModelButton";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import BomViewer from "../BomViewer/BomViewer";
import { MACHINE_NODE_PREFIX } from "../../utilities/bomUtils";
import { countAvailableModelsFromModelData } from "../../utilities/selectBestModel";
import { useOptions } from "../../context/optionContext";
import {
  buildExternalLoginUrl,
  buildExternalTargetUrl,
  getMachineDocEntry,
  openExternalDocumentation,
} from "../../utilities/externalDocs";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const MachineListItem = ({ machine }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedSite, selectedSiteData } = useSite();
  const { optionKeys } = useOptions();
  const machineSellNotifyEnabled = String(optionKeys?.enableMachineSellNotifyForm ?? "false").trim().toLowerCase() === "true";
  const [noProductsOpen, setNoProductsOpen] = useState(false);
  const [machineNotifyOpen, setMachineNotifyOpen] = useState(false);
  const [loadingResources, setLoadingResources] = useState(true);
  const [has3DModel, setHas3DModel] = useState(false);
  const [has3DModelCP, setHas3DModelCP] = useState(false);
  const [has2DModel, setHas2DModel] = useState(false);
  const [hasMultipleModels, setHasMultipleModels] = useState(false);
  const [bomModalOpen, setBomModalOpen] = useState(false);
  const [bomLoading, setBomLoading] = useState(false);
  const [bomTree, setBomTree] = useState([]);
  const [bomError, setBomError] = useState("");
  const [bomExpandedNodes, setBomExpandedNodes] = useState([]);
  const [bomSelectedNodeId, setBomSelectedNodeId] = useState(null);
  const { stockImageUrl } = useBrandingAssets();

  const normalizedMachine = useMemo(
    () => ({
      ...machine,
      machine_name:
        machine?.machine_name || machine?.name || machine?.machine_product_name || "",
      machine_number:
        machine?.machine_number || machine?.sub_name || machine?.machine_serial_number || "",
    }),
    [machine]
  );
  const machineNodeId = useMemo(
    () => `${MACHINE_NODE_PREFIX}-${machine?.machine_id || "search-list-item"}`,
    [machine?.machine_id]
  );
  const machineProductCountRaw = machine?.bom_part_count;
  const hasBomCount = machineProductCountRaw !== null && machineProductCountRaw !== undefined;
  const machineProductCount = Number(machineProductCountRaw ?? 0);
  const machineHasProducts = hasBomCount ? machineProductCount > 0 : true;
  const machineModelData = useMemo(() => {
    const raw = machine?.model_data;
    if (!raw) return {};
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }
    return typeof raw === "object" ? raw : {};
  }, [machine?.model_data]);

  const machineImage = (() => {
    const raw =
      normalizedMachine?.asset_data?.images?.[0] ||
      machineModelData?.images?.[0] ||
      machineModelData?.all_images?.[0];
    if (raw) {
      if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
      const path = raw.startsWith("/") ? raw : `/${raw}`;
      return `${ASSETS_BASE_URL}${path}`;
    }
    return `${ASSETS_BASE_URL}/MachineImages/${normalizedMachine.machine_number}.png`;
  })();

  const machineSerialForDocs =
    normalizedMachine?.machine_product_name ||
    normalizedMachine?.machine_serial_number ||
    normalizedMachine?.machine_number ||
    "";
  const locationIdForDocs =
    selectedSiteData?.customer_site_number ||
    selectedSiteData?.customer_site_name ||
    selectedSite ||
    "";
  const machineDocEntry = useMemo(
    () =>
      getMachineDocEntry({
        locationId: locationIdForDocs,
        serialNumber: machineSerialForDocs,
      }),
    [locationIdForDocs, machineSerialForDocs]
  );
  // Mirrors MachineCard exactly: when internal documentation is in use, the icon is
  // gated on the machine actually having documents. This used to be a bare `true`,
  // so search results showed a documentation icon for machines the My Machines page
  // showed none for. Undefined/null still falls back to showing it, which keeps
  // callers that do not supply the flag (the ChatBot machine list) unchanged.
  const internalDocAvailability = machine?.has_document ?? machine?.has_documents;
  const shouldShowDocIcon = DOCUMENT_EXTERNAL_ENABLED
    ? machineDocEntry?.HasDoc === "Y"
    : internalDocAvailability === undefined || internalDocAvailability === null
      ? true
      : internalDocAvailability === true || internalDocAvailability === "true" || internalDocAvailability === "Y" || internalDocAvailability === 1;
  const showModelIcon = has3DModel || has2DModel;
  const showDocumentationIcon = shouldShowDocIcon;
  const showBomIcon = machineHasProducts;

  const normalize3DFile = (file) => {
    if (!file) return { glb: null, html: null };
    if (typeof file === "string") return { glb: null, html: file };
    return {
      glb: file.glb || null,
      html: file.html || null,
    };
  };

  useEffect(() => {
    setLoadingResources(true);
    try {
      const modelData = machineModelData || {};
      const main3d = normalize3DFile(modelData["3dfile"]);
      const has3D = Boolean(main3d.glb || main3d.html || modelData.splitfiles?.length > 0);
      const has3DCP = !!modelData["3d_cp"];
      const has2D = !!modelData["2dfile"];
      const bomModels = modelData.boms?.filter((bom) =>
        Boolean(normalize3DFile(bom["3dfile"]).glb || normalize3DFile(bom["3dfile"]).html || bom["2dfile"])
      ) || [];
      const hasBOM2D = bomModels.some((bom) => bom["2dfile"]);
      const hasBOM3D = bomModels.some((bom) => {
        const threeD = normalize3DFile(bom["3dfile"]);
        return Boolean(threeD.glb || threeD.html);
      });
      const modelCount = countAvailableModelsFromModelData(modelData);
      setHas3DModel(has3D || hasBOM3D);
      setHas3DModelCP(has3DCP);
      setHas2DModel(has2D || hasBOM2D);
      setHasMultipleModels(modelCount > 1);
    } catch {
      setHas3DModel(false);
      setHas3DModelCP(false);
      setHas2DModel(false);
      setHasMultipleModels(false);
    } finally {
      setLoadingResources(false);
    }
  }, [machineModelData]);

  useEffect(() => {
    setBomTree([]);
    setBomSelectedNodeId(null);
    setBomExpandedNodes([machineNodeId]);
  }, [machineNodeId, selectedSite]);

  useEffect(() => {
    if (!bomModalOpen) {
      setBomSelectedNodeId(null);
    }
  }, [bomModalOpen]);

  const handleNavigateMachine = async () => {
    if (!machine?.machine_id) {
      return;
    }

    if (!selectedSite) {
      Swal.fire({
        icon: "warning",
        title: t("ALERT_MESSAGES.WARNING_TITLE"),
        text: t("ALERT_MESSAGES.SELECT_SITE_FIRST", { defaultValue: "Please select a site first." }),
      });
      return;
    }

    try {
      const response = await getProductsByMachineId({
        machine_id: machine.machine_id,
        site_number: selectedSite,
      });

      const { products } = normalizeMachineProductsResponse(response);

      if (Array.isArray(products) && products.length > 0) {
        navigate(`/machines/${machine.machine_id}`);
      } else {
        setNoProductsOpen(true);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("ALERT_MESSAGES.ERROR"),
        text: t("ALERT_MESSAGES.FAILED_DATA", { defaultValue: "Failed to load machine details." }),
      });
    }
  };

  const handleOpenBomModal = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!machineHasProducts || !machine?.machine_id || !selectedSite) return;
    setBomModalOpen(true);
    if (bomTree.length > 0) return;

    setBomLoading(true);
    setBomError("");
    try {
      const response = await getProductsByMachineId({
        machine_id: machine.machine_id,
        site_number: selectedSite,
      });
      if (response.status) {
        const { bomTree: fetchedTree } = normalizeMachineProductsResponse(response);
        setBomTree(fetchedTree || []);
        setBomExpandedNodes([machineNodeId]);
        setBomSelectedNodeId(null);
      } else {
        setBomTree([]);
        setBomError(t("ALERT_MESSAGES.FAILED_DATA"));
      }
    } catch {
      setBomTree([]);
      setBomError(t("ALERT_MESSAGES.FAILED_DATA"));
    } finally {
      setBomLoading(false);
    }
  };

  const handleNavigateToBomProduct = (productId) => {
    if (!productId) return;
    setBomModalOpen(false);
    navigate(`/machines/${machine.machine_id}/${productId}`);
  };

  const handleDocumentationClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!DOCUMENT_EXTERNAL_ENABLED) {
      navigate(`/documentation/${machine.machine_id}`);
      return;
    }
    if (!machineDocEntry || machineDocEntry.HasDoc !== "Y") return;
    const username = selectedSiteData?.amf_method_username_ln;
    const password = selectedSiteData?.amf_method_password_ln;
    const loginUrl = buildExternalLoginUrl({
      templateUrl: DOCUMENT_EXTERNAL_URL,
      username,
      password,
    });
    const targetUrl = buildExternalTargetUrl({
      username,
      group: machineDocEntry.Group,
      machine: machineSerialForDocs,
    });
    if (!loginUrl || !targetUrl) {
      navigate(`/documentation/${machine.machine_id}`);
      return;
    }
    openExternalDocumentation({ loginUrl, targetUrl });
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "75px",
          borderRadius: 2,
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "stretch",
          backgroundColor: "#fff",
          boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
          p: 2,
          columnGap: 2,
          margin: "7px 0",
          "&:hover": {
            boxShadow: "0 0 10px 2px rgba(0,0,0,0.2)",
          },
          cursor: "pointer",
        }}
        onClick={handleNavigateMachine}
      >
        <img
          style={{ height: "50px", width: "50px", flexShrink: 0 }}
          src={machineImage}
          alt={normalizedMachine.machine_name}
          onError={(e) => {
            e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
          {/* width:100% + the parent's minWidth:0 lets the name run all the way to the
              action icons before it ellipses, instead of stopping at a fixed maxWidth. */}
          <Tooltip title={normalizedMachine.machine_name}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {normalizedMachine.machine_name}
            </Typography>
          </Tooltip>

          <Tooltip title={`${t("UI_TEXT.MACHINE")} ${normalizedMachine.machine_number || ""}`}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: "11px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              <b>{t("UI_TEXT.MACHINE")} </b> {normalizedMachine.machine_number}
            </Typography>
          </Tooltip>
        </Box>

        <Box display="flex" justifyContent="flex-end" alignItems="center" columnGap={0.5} sx={{ flexShrink: 0 }}>
          {loadingResources ? (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Skeleton variant="circular" width={26} height={26} />
              <Skeleton variant="circular" width={26} height={26} />
              <Skeleton variant="circular" width={26} height={26} />
            </Box>
          ) : (
            <>
              {showModelIcon && (
                <ModelButton
                  machineName={normalizedMachine.machine_name}
                  machineNumber={normalizedMachine.machine_number}
                  machineProductName={normalizedMachine.machine_product_name}
                  machineid={normalizedMachine.machine_id}
                  productNumbers={normalizedMachine.product_numbers}
                  changeParts={has3DModelCP}
                  has2D={has2DModel}
                  hasMultipleModels={hasMultipleModels}
                  modelData={machineModelData}
                />
              )}
              {showDocumentationIcon && (
                <Tooltip title={t("TITLES.VIEW_DOCUMENTATION")}>
                  <IconButton
                    aria-label="Documentation"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/documentation/${machine.machine_id}`, {
                        state: {
                          machine: {
                            machine_id: machine.machine_id,
                            machine_name: normalizedMachine.machine_name,
                            machine_number: normalizedMachine.machine_number
                          }
                        }
                      });
                    }}
                  >
                    <DescriptionOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {/* No order-history icon here on purpose: MachineCard does not render one
                  (its block is commented out), and these two surfaces are meant to offer
                  the same actions for the same machine. */}
              {showBomIcon ? (
                <Tooltip title={t("UI_TEXT.VIEW_BOM")}>
                  <IconButton
                    aria-label="View BOM"
                    size="large"
                    onClick={handleOpenBomModal}
                  >
                    <PiTreeStructureFill size={20} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          )}
          {machineSellNotifyEnabled ? (
            <SpeedDial
              ariaLabel="Machine actions"
              icon={<MoreHorizIcon sx={{ color: "primary.main", fontSize: 20 }} />}
              direction="left"
              sx={{
                width: "10px",
                height: "10px",
                ml: 1,
                "& .MuiFab-primary": {
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  minHeight: 30,
                  height: 30,
                  width: 30,
                  "&:hover": { backgroundColor: "transparent" },
                },
              }}
            >
              <SpeedDialAction
                icon={<ContactPhoneIcon />}
                tooltipTitle={t("TITLES.MACHINE_SOLD_NOTIFY")}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMachineNotifyOpen(true);
                }}
              />
            </SpeedDial>
          ) : null}
        </Box>
      </Box>
      <ContactCCRForm open={noProductsOpen} setOpen={setNoProductsOpen} machine={normalizedMachine} />
      {machineSellNotifyEnabled && (
        <MachineNotifyForm
          open={machineNotifyOpen}
          setOpen={setMachineNotifyOpen}
          machine_number={normalizedMachine.machine_number}
          machine_name={normalizedMachine.machine_name}
        />
      )}
      <ModalWrapper
        open={bomModalOpen}
        setOpen={setBomModalOpen}
        title={t("UI_TEXT.BOM_TREE_MODAL_TITLE")}
        subtitle={t("UI_TEXT.BOM_TREE_MODAL_SUBTITLE")}
        width="60vw"
        content={
          bomLoading ? (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              {Array.from({ length: 8 }).map((_, idx) => (
                <Skeleton key={idx} variant="rounded" height={28} />
              ))}
            </Box>
          ) : bomError ? (
            <Alert severity="error">{bomError}</Alert>
          ) : (
            <BomViewer
              title={t("UI_TEXT.BOM_TREE_TITLE")}
              helperText={t("UI_TEXT.BOM_TREE_HELPER")}
              clearSelectionLabel={t("UI_TEXT.CLEAR_BOM_SELECTION")}
              noDataLabel={t("UI_TEXT.NO_BOM_DATA")}
              machineName={normalizedMachine.machine_name}
              machineNumber={normalizedMachine.machine_number}
              machineNodeId={machineNodeId}
              treeData={bomTree}
              selectedNodeId={bomSelectedNodeId}
              expandedNodes={bomExpandedNodes}
              checkedProductIds={[]}
              onNodeSelect={setBomSelectedNodeId}
              onClearSelection={() => setBomSelectedNodeId(null)}
              onToggle={setBomExpandedNodes}
              variant="modal"
              isLoading={bomLoading}
              bomSearchPlaceholder={t("UI_TEXT.BOM_TREE_SEARCH_PLACEHOLDER")}
              showCheckboxes={false}
              onNavigateToProduct={handleNavigateToBomProduct}
              machineHasDocumentation={shouldShowDocIcon}
              onMachineDocumentationClick={handleDocumentationClick}
            />
          )
        }
      />
    </>
  );
};

export default MachineListItem;
